import React, { useEffect, useRef } from "react";
import headerImage from "./assets/vitrine/school/header.png";
import section1Video from "./assets/vitrine/school/section1.mp4";
import section3Image from "./assets/vitrine/school/section3.png";
import section4Video from "./assets/vitrine/school/section4.mp4";
import section5Image from "./assets/vitrine/school/section5.png";
import section6Image from "./assets/vitrine/school/section6.png";
import section8Image from "./assets/vitrine/school/section8.png";
import "./SchoolPage.css";

const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=com.akremanaya.weavevomobile&hl=fr";

function HoverVideo({ src }) {
  const ref = useRef(null);

  const playWithSound = () => {
    const video = ref.current;
    if (!video) return;
    video.muted = false;
    const promise = video.play();
    if (promise?.catch) promise.catch(() => {});
  };

  const keepPlayingMuted = () => {
    const video = ref.current;
    if (!video) return;
    video.muted = true;
    const promise = video.play();
    if (promise?.catch) promise.catch(() => {});
  };

  return (
    <div className="school-video" onMouseEnter={playWithSound} onMouseLeave={keepPlayingMuted}>
      <video ref={ref} autoPlay muted loop playsInline controls preload="auto">
        <source src={src} type="video/mp4" />
      </video>
    </div>
  );
}

function Detail({ label, children }) {
  return (
    <p className="school-detail">
      <strong>{label}</strong> {children}
    </p>
  );
}

export default function SchoolPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="school-page">
      <section className="school-hero">
        <img src={headerImage} alt="" />
        <div className="school-heroOverlay" />
        <div className="school-heroCopy">
          <h1>Stop Freezing Knowledge into Rigid Formats.</h1>
          <p>Free academic learning from inflexible, uneditable 45-minute lecture recordings. With The Loom, faculty and students co-create modular learning streams: update a single concept, lab demonstration, or exercise in one click without re-recording the entire course.</p>
        </div>
      </section>

      <section className="school-section school-sectionIntro">
        <div className="school-copy school-copyWide">
          <h2>Collaborative Campus Tours &amp; Open Days</h2>
          <Detail label="Audience:">Administration &amp; Student Ambassadors</Detail>
          <Detail label="Recommended Layout:">Storyboard or Gallery</Detail>
          <Detail label="The Challenge:">Traditional promotional videos cost thousands of euros and become obsolete as soon as a building is renovated or campus facilities evolve.</Detail>
          <Detail label="Collaborative Workflow:">The communications team creates a central Weave. Student ambassadors across campus claim dedicated slots to film lecture halls, research labs, dining facilities, and athletic grounds.</Detail>
          <Detail label="The Living Weave Advantage (Hot-Swap):">When a new maker-space opens or an association changes leadership, only that specific slot is re-filmed and updated in place—preserving printed brochure QR codes, links, and cumulative views.</Detail>
        </div>
        <HoverVideo src={section1Video} />
      </section>

      <section className="school-section school-sectionPlain">
        <div className="school-copy school-copyWide">
          <h2>Modular Flipped Classroom &amp; Video Micro-Learning</h2>
          <Detail label="Audience:">Faculty &amp; Instructors</Detail>
          <Detail label="Recommended Layout:">Storyboard</Detail>
          <Detail label="The Challenge:">Lengthy 45-minute lecture videos have low student retention, take days to produce, and require complete re-recording whenever a syllabus item or law changes.</Detail>
          <Detail label="Collaborative Workflow:">The professor breaks down a syllabus chapter into 5 concise slots (e.g., 1. Problem statement, 2. Core theorem, 3. Real-world case, 4. Step-by-step proof, 5. Practical exercise). Teaching assistants or students can co-produce demonstrative slots.</Detail>
          <Detail label="The Living Weave Advantage:">Whenever a regulation, dataset, or textbook reference is revised, the professor swaps out the outdated 30-second slot in minutes without re-exporting the entire course or modifying links on the LMS (Moodle, Canvas).</Detail>
        </div>
      </section>

      <section className="school-section school-sectionSplit">
        <figure className="school-media school-mediaPortrait">
          <img src={section3Image} alt="" loading="lazy" />
        </figure>
        <div className="school-copy">
          <h2>The Interactive Student Onboarding &amp; Digital Campus Guide</h2>
          <Detail label="Audience:">School Administration &amp; Student Affairs</Detail>
          <Detail label="Recommended Layout:">Storyboard</Detail>
          <Detail label="The Challenge:">Dense 50-page PDF welcome guides and static institutional videos are largely ignored by incoming domestic and international cohorts.</Detail>
          <Detail label="Collaborative Workflow:">Departments co-create an interactive guide: Academic Affairs details registration, IT outlines campus Wi-Fi access, the Health Center presents medical services, and the Student Union introduces campus clubs.</Detail>
          <Detail label="The Living Weave Advantage:">When campus desk hours change or procedures are updated mid-year, the relevant slot is refreshed instantly. The onboarding QR code printed on student IDs remains permanently valid.</Detail>
        </div>
      </section>

      <section className="school-section school-sectionMediaFirst">
        <HoverVideo src={section4Video} />
        <div className="school-copy school-copyWide">
          <h2>Public Speaking, Debates &amp; Language Fluency Workshops</h2>
          <Detail label="Audience:">Faculty &amp; Language / Humanities Students</Detail>
          <Detail label="Recommended Layout:">Versus or Seed</Detail>
          <Detail label="The Challenge:">Providing individual, actionable feedback on verbal articulation and debate posture in overcrowded language or communication seminars is difficult.</Detail>
          <Detail label="Collaborative Workflow:">The instructor issues a weekly debate prompt (Seed). Students submit 20-second structured arguments filmed directly on mobile.</Detail>
          <Detail label="The Living Weave Advantage:">Using the split-screen Versus layout, professors can juxtapose a student’s baseline presentation from week 1 against their final performance in week 12, creating an objective visual measure of student progress.</Detail>
        </div>
      </section>

      <section className="school-section school-sectionSplit school-sectionSplitReverse">
        <div className="school-copy">
          <h2>Alumni Impact &amp; Career Pathways (&quot;Voices of Graduates&quot;)</h2>
          <Detail label="Audience:">School Administration &amp; Career Centers</Detail>
          <Detail label="Recommended Layout:">Seed or Gallery</Detail>
          <Detail label="The Challenge:">Graduate employment statistics presented as spreadsheets fail to engage prospective students during recruitment campaigns.</Detail>
          <Detail label="Collaborative Workflow:">The alumni office dispatches invitation links to graduates worldwide. Each alumnus films their current workspace, describes their job responsibilities, and shares advice for incoming students.</Detail>
          <Detail label="The Living Weave Advantage:">The school builds an authentic, decentralized alumni map spanning global hubs (Paris, London, New York, Tokyo) that can be projected at student fairs and updated continually without new production budgets.</Detail>
        </div>
        <figure className="school-media">
          <img src={section5Image} alt="" loading="lazy" />
        </figure>
      </section>

      <section className="school-section school-sectionImageLead">
        <figure className="school-media school-mediaWide">
          <img src={section6Image} alt="" loading="lazy" />
        </figure>
        <div className="school-copy school-copyWide">
          <h2>International Exchange Programs &amp; Erasmus Visual Exchanges</h2>
          <Detail label="Audience:">Students &amp; International Relations Offices</Detail>
          <Detail label="Recommended Layout:">Versus or Relay</Detail>
          <Detail label="The Challenge:">Coordinating meaningful, asynchronous collaborative work between partner universities across borders is operationally complex.</Detail>
          <Detail label="Collaborative Workflow:">Partner classes in different countries (e.g., France, Germany, Japan) co-create a mirrored cultural Weave, matching daily routines, studio work, and local traditions slot-by-slot.</Detail>
          <Detail label="The Living Weave Advantage:">Multilingual speech synthesis automatically subtitles and translates each segment into both target languages, bridging communication barriers without manual dubbing.</Detail>
        </div>
        <div className="school-copy school-copyWide school-copySecond">
          <h2>Cross-Disciplinary Expeditions &amp; Field Research</h2>
          <Detail label="Audience:">Faculty &amp; Student Teams</Detail>
          <Detail label="Recommended Layout:">Relay or Storyboard</Detail>
          <Detail label="The Challenge:">Academic disciplines operate in silos, rarely producing shared, unified project documentation during fieldwork.</Detail>
          <Detail label="Collaborative Workflow:">Faculty members from distinct departments (e.g., Earth Sciences, History, and Foreign Languages) run a joint field study. Science students document soil sampling, history students record site heritage, and language students record translated audio tracks.</Detail>
          <Detail label="The Living Weave Advantage:">The Loom automatically balances audio, smooths visual transitions between multi-camera phone captures, and renders a unified cross-departmental research documentary.</Detail>
        </div>
      </section>

      <section className="school-section school-sectionPlain">
        <div className="school-copy school-copyWide">
          <h2>Safety Instructions, Regulations &amp; Emergency Protocols (PPMS / HSE)</h2>
          <Detail label="Recommended Layout:">Storyboard</Detail>
          <Detail label="The Challenge:">Fire evacuation and safety procedures (PPMS) remain static posters placed behind doors that no one reads.</Detail>
          <Detail label="Collaborative Workflow:">Safety managers (HSE) and class representatives create a video divided into rapid-action slots: Assembly Point, Emergency Exits, Defibrillator Locations, and Emergency Numbers.</Detail>
          <Detail label="The Living Weave Advantage:">When a building is reconfigured or work affects an exit, the video evacuation plan is updated immediately. The administration maintains impeccable regulatory traceability for the regional education authority and safety commissions.</Detail>
        </div>
      </section>

      <section className="school-section school-sectionSplit">
        <figure className="school-media">
          <img src={section8Image} alt="" loading="lazy" />
        </figure>
        <div className="school-copy">
          <h2>Interactive Archiving of Masterclasses &amp; Expert Keynotes</h2>
          <Detail label="Recommended Layout:">Symphony or Gallery</Detail>
          <Detail label="The Challenge:">Full-length two-hour recordings of guest speaker keynotes end up buried deep inside cloud drives, rarely ever re-watched or repurposed.</Detail>
          <Detail label="Collaborative Workflow:">During the conference, a designated group of students captures key highlights distributed into thematic slots (e.g., the core thesis, the most provocative audience question, the expert’s rebuttal, and the final takeaway).</Detail>
          <Detail label="The Living Weave Advantage:">The AI extracts peak moments, balances audio levels with an unobtrusive background score, and automatically compiles an engaging 3-minute living synthesis—instantly indexable within academic media libraries for future cohorts to reference.</Detail>
        </div>
      </section>

      <section className="school-section school-sectionPlain">
        <div className="school-copy school-copyWide">
          <h2>Campus Sustainability Challenges &amp; ESG Reporting</h2>
          <Detail label="Audience:">Administration, Faculty &amp; Student Clubs</Detail>
          <Detail label="Recommended Layout:">Seed</Detail>
          <Detail label="The Challenge:">Institutional sustainability audits (green campus labels, carbon footprint goals) often lack visual, community-backed evidence.</Detail>
          <Detail label="Collaborative Workflow:">During a campus climate initiative, a campus-wide call (Seed) invites students and staff to document concrete green actions: bike-to-work routines, lab recycling protocols, and campus community gardens.</Detail>
          <Detail label="The Living Weave Advantage:">A verified mosaic of concrete campus initiatives is compiled in real time. Outdated initiatives are hot-swapped as environmental milestones are reached, creating an auditable ESG living record.</Detail>
        </div>
      </section>

      <section className="school-section school-sectionPlain school-sectionLast">
        <div className="school-copy school-copyWide">
          <h2>Cross-Disciplinary Joint Projects (e.g., Science + History + Languages)</h2>
          <Detail label="Recommended Layout:">Relay or Versus</Detail>
          <Detail label="The Challenge:">Academic subject silos prevent cross-curricular collaborative projects due to the lack of a shared platform for unified student presentation.</Detail>
          <Detail label="Collaborative Workflow:">Two or three instructors launch a joint initiative (e.g., &quot;The Impact of the Industrial Revolution on the Mining Basin&quot;). The history teacher manages historical archive slots, the earth/environmental sciences teacher oversees field soil sampling slots, and the language teacher supervises the synthetic voice-over script in English.</Detail>
          <Detail label="The Living Weave Advantage:">Students collaborate on a single shared timeline. The Loom automatically harmonizes sound design and unifies visual transitions, producing a cohesive master cut that can be jointly assessed and graded by all participating faculty members.</Detail>
        </div>
      </section>

      <section className="school-cta">
        <h2>Put the Power of the Living Weave into Your Cohorts&apos; Hands</h2>
        <p>A secure, GDPR-compliant mobile workspace with automated AI moderation to coordinate campus showcases, celebrate alumni, and energize academic learning.</p>
        <div className="school-storeActions">
          <a href={GOOGLE_PLAY_URL} target="_blank" rel="noreferrer" className="school-storeButton school-storeButtonPrimary">
            <i className="bi bi-google-play" aria-hidden="true" />
            <span>Play Store</span>
          </a>
          <button type="button" className="school-storeButton school-storeButtonSecondary" disabled>
            <i className="bi bi-apple" aria-hidden="true" />
            <span>App Store</span>
          </button>
        </div>
      </section>
    </main>
  );
}
